<template>
	<div class="region-selector">
		<!-- 二级联动布局 -->
		<div class="region-wrapper">
			<!-- 第二级 -->
			<div class="region-level level2">
				<div v-for="(item, index) in level2" :key="index" class="level-item" :class="{ active: activeLevel2 === index }"
					@click="switchLevel2(index)">
					{{ item.name }}
					<span class="tip-num" v-if="levelSelectedMap[item.id]">({{ levelSelectedMap[item.id] }})</span>
				</div>
			</div>

			<!-- 第三级（可多选 + 顶部全选） -->
			<div class="region-level level3">
				<div class="level-item all-select" @click="selectAllLevel3">
					<div class="checkmark" :class="{ active: isAllSelected }"></div>
					全选
				</div>
				<div v-for="(item, index) in level3" :key="index" class="level-item" @click="toggleLevel3(item)">
					<div class="checkmark" :class="{ active: selectedIds.includes(item.id) }"></div>
					{{ item.name }}
				</div>
			</div>
		</div>

		<!-- 底部按钮 -->
		<div class="region-footer">
			<div class="selected-count">已选 {{ selectedList.length }} 个</div>
			<div class="flex-center">
				<el-button @click="clearAll">清空已选</el-button>
				<el-button type="primary" @click="confirmSelected">确定</el-button>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: "OptionCircle",
		props: {
			list: {
				type: Array,
				required: true,
				default: () => []
			},
			// 默认选中的ID数组
			defaultSelectedIds: {
				type: Array,
				default: () => []
			},
			// 默认选中的完整数据
			defaultSelectedItems: {
				type: Array,
				default: () => []
			}
		},

		watch: {
			list: {
				handler(newVal) {
					if (newVal && newVal.length > 0) {
						this.initData()
					}
				},
				deep: true,
				immediate: true
			},
			// 监听默认值变化
			defaultSelectedIds: {
				handler(newVal) {
					if (newVal && newVal.length > 0 && this.level2.length > 0) {
						this.setDefaultSelected(newVal)
					}
				},
				deep: true
			}
		},

		data() {
			return {
				level2: [], // 第二级列表
				level3: [], // 第三级列表
				activeLevel2: 0, // 第二级选中索引
				selectedIds: [], // 第三级选中ID
				selectedList: [] // 第三级选中完整数据
			};
		},

		computed: {
			isAllSelected() {
				if (this.level3.length === 0) return false
				return this.level3.every(item => this.selectedIds.includes(item.id))
			},
			levelSelectedMap() {
				const map = {}
				const sid = new Set(this.selectedIds)
				this.level2.forEach(l2 => {
					let c2 = 0
					if (l2.children) {
						l2.children.forEach(l3 => {
							if (sid.has(l3.id)) c2++
						})
					}
					map[l2.id] = c2
				})
				return map
			}
		},

		created() {
			this.initData();
		},

		methods: {
			// 初始化：直接加载第二级
			initData() {
				if (!this.list || this.list.length === 0) return
				// 直接取第一级的 children 作为第二级
				this.level2 = this.list || []
				if (this.level2.length > 0) {
					this.switchLevel2(0)
					// 如果有默认值，设置默认选中
					if (this.defaultSelectedIds && this.defaultSelectedIds.length > 0) {
						this.$nextTick(() => {
							this.setDefaultSelected(this.defaultSelectedIds)
						})
					}
				}
			},

			// 设置默认选中
			setDefaultSelected(ids) {
				if (!ids || ids.length === 0) return

				// 递归查找所有要选中的项
				const findAllItems = (items, targetIds, result = []) => {
					for (const item of items) {
						if (targetIds.includes(item.id)) {
							result.push(item)
						}
						if (item.children && item.children.length) {
							findAllItems(item.children, targetIds, result)
						}
					}
					return result
				}

				const selectedItems = findAllItems(this.level2, ids)
				this.selectedIds = [...ids]
				this.selectedList = selectedItems

				// 触发 confirm 事件，通知父组件
				this.$emit("confirm", this.getSelectedData())
			},

			// 切换第二级 → 加载第三级
			switchLevel2(index) {
				this.activeLevel2 = index
				const item = this.level2[index]
				this.level3 = item.children || []
			},

			// 第三级 单选/取消
			toggleLevel3(item) {
				const index = this.selectedIds.indexOf(item.id)
				if (index > -1) {
					this.selectedIds.splice(index, 1)
					this.selectedList = this.selectedList.filter(i => i.id !== item.id)
				} else {
					this.selectedIds.push(item.id)
					this.selectedList.push(item)
				}
			},

			// 第三级 当前组 全选
			selectAllLevel3() {
				// 获取当前二级项的所有三级项ID
				const currentLevel3Ids = this.level3.map(i => i.id)
				const currentLevel3Items = [...this.level3]

				// 检查当前组是否已经全选
				const isCurrentGroupAllSelected = currentLevel3Ids.every(id => this.selectedIds.includes(id))

				if (isCurrentGroupAllSelected) {
					// 如果当前组已全选，则取消选中当前组的所有项
					this.selectedIds = this.selectedIds.filter(id => !currentLevel3Ids.includes(id))
					this.selectedList = this.selectedList.filter(item => !currentLevel3Ids.includes(item.id))
				} else {
					// 如果当前组未全选，则添加当前组所有未选中的项
					const newIds = currentLevel3Ids.filter(id => !this.selectedIds.includes(id))
					const newItems = currentLevel3Items.filter(item => !this.selectedList.some(i => i.id === item.id))
					this.selectedIds.push(...newIds)
					this.selectedList.push(...newItems)
				}
			},

			// 删除指定项
			deleteSelectedItem(item) {
				const index = this.selectedIds.indexOf(item.id)
				if (index > -1) {
					this.selectedIds.splice(index, 1)
					this.selectedList = this.selectedList.filter(i => i.id !== item.id)
				}
				this.confirmSelected()
			},

			// 清空
			clearAll() {
				this.selectedIds = []
				this.selectedList = []
				this.$emit("clear")
				this.$emit("confirm", this.getSelectedData())
			},

			// 清空所有选中（外部调用）
			clearAllSelected() {
				this.selectedIds = []
				this.selectedList = []
			},

			// 获取选中的数据格式
			getSelectedData() {
				return {
					totalCount: this.selectedList.length,
					groups: [{
						name: '位置',
						selectedItems: this.selectedList,
						selectedIds: this.selectedIds.join(',')
					}]
				}
			},

			// 确定
			confirmSelected() {
				this.$emit("confirm", this.getSelectedData())
			}
		}
	};
</script>

<style scoped lang="scss">
	.region-selector {
		width: 700px;
		padding: 5px;
		background: #fff;
		border-radius: 8px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
	}

	.region-wrapper {
		display: flex;
		gap: 12px;
		margin-bottom: 15px;
		max-height: 400px;
		overflow-y: auto;
	}

	.region-level {
		flex: 1;
		display: flex;
		flex-direction: column;
		padding: 8px;
		border-radius: 6px;
		height: 300px;
		overflow-y: auto;

		.tip-num {
			font-size: 12px;
			color: #999;
		}
	}

	.level2 {
		border-left: 1px solid #eee;
		border-right: 1px solid #eee;
	}

	.level-item {
		padding: 8px 12px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
		transition: 0.2s;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.level-item:hover {
		background: #f5f5f5;
	}

	.region-level:not(.level3) .level-item.active {
		background: #e6f0ff;
		color: #28b05e;
	}

	.checkmark {
		width: 16px;
		height: 16px;
		border: 1px solid #d9d9d9;
		border-radius: 2px;
		margin-right: 8px;
		display: flex;
		align-items: center;
		justify-content: center;

		&.active {
			background: #28b05e;
			border-color: #28b05e;

			&::after {
				content: '✔';
				color: #fff;
				font-size: 12px;
			}
		}
	}

	.all-select {
		font-weight: 500;
		margin-bottom: 4px;
	}

	.region-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-top: 1px solid #f0f0f0;

		.el-button {
			border-radius: 8px;
			width: 90px;

			&--primary {
				border: 1px solid #28b05e;
				background-color: #28b05e;
				color: #fff;
			}
		}
	}

	.selected-count {
		color: #666;
		font-size: 14px;
	}
</style>
